import "./bootstrap";
import "../css/app.css";

import { createApp, h } from "vue";
import { createInertiaApp } from "@inertiajs/vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { ZiggyVue } from "../../vendor/tightenco/ziggy";
import { ZiggyUpdaterPlugin } from "./Plugins/ZiggyUpdater";
import { createPinia } from "pinia";

const appName = window.runtimeConfig?.appName || '';

//Pinia - Start
const pinia = createPinia();

//Font Awesome - Start
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { faHouse, faPlay } from "@fortawesome/pro-solid-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
library.add(fal, faHouse, faPlay, faYoutube);
//Font Awesome - End

//Vapor - Start
import Vapor from "laravel-vapor";
Vapor.withBaseAssetUrl(import.meta.env.VITE_VAPOR_ASSET_URL);
window.Vapor = Vapor;
//Vapor - End

//Bugsnag - Start
let bugsnagVue = null;
const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
const isLocalDev = window.location.hostname === "localhost" || window.location.hostname.endsWith(".test");
if (!isLocalDev && bugsnagApiKey) {
    Bugsnag.start({
        apiKey: bugsnagApiKey,
        plugins: [new BugsnagPluginVue()],
        onError: (event) => {
            const error = event.errors?.[0];
            const message = error?.errorMessage ?? "";

            // "ResizeObserver loop completed with undelivered notifications" is a
            // benign browser notification (not a crash) emitted by libraries such
            // as mux-player; discard it so it doesn't drown out real errors.
            if (message.startsWith("ResizeObserver loop")) {
                return false;
            }

            // The Google Search App's in-app browser injects an accessibility/
            // preview script that recurses into a deep DOM and blows the stack
            // (findTopmostVisibleElement -> checkForImage -> isImage). We don't
            // ship that script, so the report is unactionable noise.
            if (error?.errorClass === "RangeError"
                && error.stacktrace?.some((frame) => frame?.method === "findTopmostVisibleElement")) {
                return false;
            }
        },
    });
    bugsnagVue = Bugsnag.getPlugin("vue");
}
//Bugsnag - End

// After a Vapor deploy, S3 prunes the previous build's hashed chunks. Tabs
// still running the old manifest fail to import dynamic chunks like
// Chats-XYZ.js; the only safe recovery is a reload onto the new manifest.
// Vite emits this event before the import promise rejects.
window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    window.location.reload();
});

//AutoAnimate - Start
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
//AutoAnimate - End

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        try {
            const component = await resolvePageComponent(
                `./Pages/${name}.vue`,
                import.meta.glob("./Pages/**/*.vue")
            );
            // Defence-in-depth alongside the vite:preloadError listener above:
            // if Vite resolves the dynamic import to undefined (a stale-chunk
            // path that doesn't trigger the documented preloadError event,
            // observed once in Bugsnag inside Inertia's resolveComponent), we
            // can't let Inertia crash on module.default — reload onto the new
            // build manifest instead.
            if (!component) {
                window.location.reload();
                throw new Error(`Inertia page resolver returned undefined for ${name}`);
            }
            return component;
        } catch (err) {
            // Same recovery for synchronous "Page not found" / chunk fetch
            // failures that escape the preloadError listener.
            window.location.reload();
            throw err;
        }
    },
    setup({ el, App, props, plugin }) {
        const appInstance = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue, props.initialPage.props.ziggy)
            .use(ZiggyUpdaterPlugin)
            .use(pinia)
            .use(autoAnimatePlugin)
            .mixin({
                methods: { asset: window.Vapor.asset },
            })
            .component("font-awesome-icon", FontAwesomeIcon);

        // Only use the Bugsnag plugin if it's not null
        if (bugsnagVue) {
            appInstance.use(bugsnagVue);
        }

        appInstance.mount(el);
    },
    progress: {
        color: import.meta.env.VITE_APP_COLOUR,
    },
});
